import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Product } from '@kimuno/api-sdk/lib/types/responses/Product';
import { Subject, dashboardSection, routes } from '../../../constants';
import { SearchFilters, SearchResultsTab } from '../../../types/search';
import useRouter from '../../../utils/useRouter';
import DashboardMuiCard from '../../cards/MuiCard/DashboardMuiCard';
import { getCommonCardProps } from '../../Search/SearchResults/SearchResults';
import { AsyncActionStatus } from '../../../actions/common.actionTypes';
import { buildProfileFilterCascade } from '../../../utils/profileFilters';
import searchFiltersToQuery from '../../../utils/searchFiltersToQuery';
import useProfileProductRow, {
  PROFILE_ROW_MIN_ITEMS,
} from '../../../routes/DashboardPage/hooks/useProfileProductRow';
import DashboardRowShell from '../DashboardRowShell/DashboardRowShell';

const STUDYFLIX_PUBLISHER = 'Studyflix';

interface OwnProps {
  // The user's top subjects, combined into one Studyflix row.
  subjects: Subject[];
  // Class years already mapped from the user's school stages (may be empty).
  classYears: number[];
  schoolType?: string | null;
}

/**
 * Profile-aware Studyflix row (PRO-4): one combined row across the user's top
 * subjects, always publisher-scoped to Studyflix, sharing the same fallback
 * cascade + hide/cap rules as the subject rows. Static title, no breadcrumb.
 */
const ProfileStudyflixRow: React.FC<OwnProps> = ({ subjects, classYears, schoolType }) => {
  const { t } = useTranslation();
  const { router } = useRouter();

  // Studyflix row combines the user's top profile subjects (already capped by the page).
  const subjectIds = useMemo(() => subjects.map((s) => s.id), [subjects]);

  const cascade = useMemo(
    () =>
      buildProfileFilterCascade({
        subject: subjectIds,
        classYears,
        schoolType,
        publisher: STUDYFLIX_PUBLISHER,
      }),
    [subjectIds, classYears, schoolType]
  );

  const { products, status, effectiveFilters, isFinalLevel } =
    useProfileProductRow(cascade);

  const isShort = products.length < PROFILE_ROW_MIN_ITEMS;
  // A level that resolved short but can still relax is a transient state — keep
  // skeletons up until the cascade settles, so we never paint the short row for one
  // frame before the advance re-fetches (mirrors ProfileSubjectRow).
  const isRelaxing = status === AsyncActionStatus.READY && isShort && !isFinalLevel;
  const isLoading =
    status === AsyncActionStatus.INITIAL ||
    status === AsyncActionStatus.LOADING ||
    isRelaxing;

  const hidden =
    !subjectIds.length ||
    status === AsyncActionStatus.ERROR ||
    (!isLoading && isFinalLevel && isShort);

  const onShowAllClick = () => {
    // "Alle anzeigen" carries the row's exact effective filters so search shows the
    // same result set the row displayed (spec: title, results, and nav share identical
    // effective filters — subject rows and Studyflix alike).
    const searchParams: SearchFilters = { ...effectiveFilters, sortBy: 'createdDate' };
    const qs = searchFiltersToQuery(searchParams);
    router.push(`/${routes.search.root}/${SearchResultsTab.PRODUCTS}?${qs}`);
  };

  return (
    <DashboardRowShell
      title={t('Videos from Studyflix for your subjects')}
      onShowAll={onShowAllClick}
      loading={isLoading}
      hidden={hidden}
    >
      {products.map((product: Product) => (
        <DashboardMuiCard
          key={product.id}
          contentType='product'
          {...getCommonCardProps({ ...product, type: 'product' })}
          redirectToMindItemId={product.mindItems[0]}
          dashboardSection={dashboardSection.studyflixVideos}
        />
      ))}
    </DashboardRowShell>
  );
};

export default ProfileStudyflixRow;
